// @flow

import React, { useState, useEffect, useCallback } from 'react';
import * as R from 'ramda';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';

import ContentContainer from '../SharedComponents/ContentContainer';
// import Badge from '@mui/material/Badge'; // (not currently used in your code)

import KeywordDetailsDialog from './KeywordDetailsDialog';
import KeywordTriggersRow from './KeywordTriggersRow';
import BotSettingsScript from './BotSettingsScript';

import { selectUser } from '../../reduxStore/modules/user';

import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';

import botSettingsContentStyles from './Styles/BotSettingsContent.Style';

// If you need to import your Redux modules or actions, do so. 
// e.g. import { someAction } from '../../reduxStore/modules/someModule';

type Props = {};

// eslint-disable-next-line react/no-multi-comp
const BotSettingsContent = (props: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // In your old code: const [store, dispatch] = useStore();
  //  => replaced by:
  const user = useSelector(selectUser);

  // Local state
  const [value, setValue] = useState(0);
  const [keywords, setKeywords] = useState([]);
  const [openEditKeywordDialog, setOpenEditKeywordDialog] = useState(false);
  const [keyword, setKeyword] = useState(null); // Selected Keyword
  const [isNewKeyword, setIsNewKeyword] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const refreshKeywordTriggers = useCallback(() => {
    if (user) {
      dispatch({
        type: 'ACTION_SET_IS_LOADING_MODULE',
        module: 'removeKeywordTrigger',
        loading: true
      });
      SPBackend.findKeywordTriggers(user)
        .then((response) => {
          const keywordList = response.data;
          log('[BotSettingsContent].[findKeywordTriggers]', keywordList);
          setKeywords(keywordList);
        })
        .catch((error) => {
          log('[BotSettingsContent].[findKeywordTriggers] error: ', error);
        })
        .finally(() => {
          dispatch({
            type: 'ACTION_SET_IS_LOADING_MODULE',
            module: 'removeKeywordTrigger',
            loading: false
          });
        });
    }
  }, [user, dispatch]);

  // Fetch keyword triggers once user is loaded
  useEffect(() => {
    if (user) {
      log('[BotSettingsContent] useEffect => user changed => fetch triggers');
      refreshKeywordTriggers();
    }
  }, [user, refreshKeywordTriggers]);

  const removeKeywordTrigger = (keywordToDelete) => {
    if (user) {
      dispatch({
        type: 'ACTION_SET_IS_LOADING_MODULE',
        module: 'removeKeywordTrigger',
        loading: true
      });
      SPBackend.removeKeywordTrigger(keywordToDelete._id, user)
        .then((response) => {
          log('[removeKeywordTrigger] response: ', response);
          setKeywords((prevKeywords) => {
            const filterById = (p) => p._id !== keywordToDelete._id;
            return R.filter(filterById, prevKeywords);
          });
          // Show snack message
          dispatch({
            type: 'ACTION_SET_SNACK_MSG',
            snackMessage: {
              message: 'Trigger Removed',
              level: 'success',
              duration: 3000
            }
          });
        })
        .catch((error) => {
          log('[removeKeywordTrigger] error: ', error);
          dispatch({
            type: 'ACTION_SET_SNACK_MSG',
            snackMessage: {
              message: 'An error occured. Please try again.',
              level: 'error',
              duration: 3000
            }
          });
        })
        .finally(() => {
          dispatch({
            type: 'ACTION_SET_IS_LOADING_MODULE',
            module: 'removeKeywordTrigger',
            loading: false
          });
        });
    }
  };

  const handleEditKeyword = (keywordPayload) => {
    if (user) {
      log('[handleEditKeyword]', keywordPayload, isNewKeyword);
      dispatch({
        type: 'ACTION_SET_IS_LOADING_MODULE',
        module: 'removeKeywordTrigger',
        loading: true
      });
      SPBackend.editKeywordTrigger(keywordPayload, user, isNewKeyword)
        .then((response) => {
          log('[handleEditKeyword] response: ', response.data);
          setKeywords(response.data);
          dispatch({
            type: 'ACTION_SET_SNACK_MSG',
            snackMessage: {
              message: 'Trigger Updated',
              level: 'success',
              duration: 3000
            }
          });
        })
        .catch((error) => {
          dispatch({
            type: 'ACTION_SET_SNACK_MSG',
            snackMessage: {
              message: error.message || 'An error occured. Please try again.',
              level: 'error',
              duration: 3000
            }
          });
          setKeyword(null);
        })
        .finally(() => {
          dispatch({
            type: 'ACTION_SET_IS_LOADING_MODULE',
            module: 'removeKeywordTrigger',
            loading: false
          });
        });
    }
  };

  const handleCloseKeywordDetailsDialog = () => {
    log('[handleCloseKeywordDetailsDialog]...');
    // re-enable credits auto-refresh
    dispatch({
      type: 'ACTION_SET_BILLING_LIVE',
      isBillingLive: true
    });
    setOpenEditKeywordDialog(false);
  };

  const handleClickEditKeyword = (kw) => {
    if (kw) {
      log('[handleClickEditKeyword]', kw);
      // disable credits auto-refresh
      dispatch({
        type: 'ACTION_SET_BILLING_LIVE',
        isBillingLive: false
      });
      setKeyword(kw);
      setIsNewKeyword(false);
      setOpenEditKeywordDialog(true);
    }
  };

  // State for the conversation script
  const [isLoadingBotSettings, setIsLoadingBotSettings] = useState(false);
  const [botScriptData, setBotScriptData] = useState(null);

  // Load bot script data if not yet loaded
  useEffect(() => {
    if (user && !botScriptData) {
      log('[BotSettingsContent] loading Bot Script...');
      setIsLoadingBotSettings(true);
      SPBackend.getAccountBotSettings(user)
        .then((response) => {
          log('[getAccountBotSettings] bot settings: ', response.data);
          setBotScriptData(response.data);
        })
        .catch((e) => {
          log('[getAccountBotSettings] error: ', e);
        })
        .finally(() => {
          setIsLoadingBotSettings(false);
        });
    }
  }, [user, botScriptData]);

  // A small Tab Panel component
  const TabPanel = ({ children, value: val, index, ...other }) => (
    <div
      aria-labelledby={`simple-tab-${index}`}
      hidden={val !== index}
      id={`simple-tabpanel-${index}`}
      role="tabpanel"
      {...other}
    >
      {val === index && (
        <Box sx={botSettingsContentStyles.boxRoot(theme)} p={3}>
          <Typography component="div">
            {children}
          </Typography>
        </Box>
      )}
    </div>
  );

  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired
  };

  const tabProps = (i) => ({
    id: `simple-tab-${i}`,
    'aria-controls': `simple-tabpanel-${i}`
  });

  return (
    <ContentContainer hasSubheader={true} sx={botSettingsContentStyles.tableContainer(theme)}>
      <Paper
        sx={botSettingsContentStyles.tabsCtr(theme)}
        elevation={1}
      >
        <AppBar
          sx={botSettingsContentStyles.appBar(theme)}
          color="default"
          position="static"
        >
          <Tabs
            aria-label="Neo The Bot"
            indicatorColor="primary"
            textColor="primary"
            value={value}
            onChange={handleChange}
          >
            <Tab
              label="CONVERSATION SCRIPT"
              {...tabProps(0)}
              sx={botSettingsContentStyles.tabBar(theme)}
            />
            <Tab
              label="KEYWORDS"
              {...tabProps(1)}
              sx={botSettingsContentStyles.tabBar(theme)}
            />
          </Tabs>
        </AppBar>

        <TabPanel index={0} value={value}>
          <BotSettingsScript
            botScriptData={botScriptData}
            setBotScriptData={setBotScriptData}
          />
        </TabPanel>

        <TabPanel index={1} value={value}>
          <KeywordTriggersRow
            keyword={keyword}
            keywords={keywords}
            setIsNewKeyword={setIsNewKeyword}
            setKeyword={handleClickEditKeyword}
            setKeywords={setKeywords}
            setOpenEditKeywordDialog={setOpenEditKeywordDialog}
          />
        </TabPanel>
      </Paper>

      <KeywordDetailsDialog
        isNewKeyword={isNewKeyword}
        keyword={keyword}
        onClose={handleCloseKeywordDetailsDialog}
        open={openEditKeywordDialog}
        removeKeywordTrigger={removeKeywordTrigger}
        setKeyword={setKeyword}
        setOpen={setOpenEditKeywordDialog}
        triggerAction={handleEditKeyword}
      />
    </ContentContainer>
  );
};

export default BotSettingsContent;